import { validators, wrapClick } from "utils";
import { useMutation } from "@apollo/client";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { Modal } from "components/layouts";
import { SuspectedIllegalityForm } from "components/forms";
import { CREATE_SUSPECTED_ILLEGALITY } from "./apollo";
import * as Yup from 'yup'

export const SuspectedIllegalityValidationSchema = Yup.object({
  region: Yup.string().required("Region is required"),
  district: Yup.string().required("District is required"),
  suspectedCulprit: Yup.object({
    fullName: Yup.string().required("Suspected Culprit's full name is required"),
    phoneNumber: Yup.string().matches(validators.PhoneNumberRegex, "Kindly enter a valid phone number").required("Suspected Culprit's phone number is required"),
    emailAddress: Yup.string().email("Kindly enter a valid email address"),
  }).required("Suspected culprit details is required"),
  location: Yup.object({
    address: Yup.string().required("Address is required"),
    community: Yup.string()
  }).required("Location details are required"),
  ghanaPostAddress: Yup.string().matches(validators.GhanaPostRegex, "Kindly enter a valid GhanaPost address").required("Kindly enter the GhanaPost address"),
  origin: Yup.string().oneOf(["ConsumptionAnalysis", "TipOff", "AnonymousTipOff", "FieldOperations"], "Kindly select a valid origin").required("Kindly provide the origin"),
  reportedBy: Yup.object({
    fullName: Yup.string()
      .when('origin', {
        is: (origin: string) => ["TipOff", "FieldOperations"].includes(origin),
        then: (schema) => schema.required("Reporter's full name is required"),
      }),
    phoneNumber: Yup.string()
      .matches(validators.PhoneNumberRegex, "Kindly enter a valid phone number")
      .when('origin', {
        is: (origin: string) => ["TipOff", "FieldOperations"].includes(origin),
        then: (schema) => schema.required("Reporter's phone number is required"),
      }),
    emailAddress: Yup.string().email("Kindly enter a valid email address"),
  })
  .when('origin', {
    is: (origin: string) => ["TipOff", "FieldOperations"].includes(origin),
    then: (schema) => schema.required("Reporter details are required"),
  }),
  supportingImageUrls: Yup.array().of(Yup.string()).max(5),
  notes: Yup.string()
})

export default function CreateSuspectedIllegalityContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const [createSuspectedIllegality] = useMutation(CREATE_SUSPECTED_ILLEGALITY);

  const form = useFormik({
    initialValues: {
      region: "",
      district: "",
      suspectedCulprit: {
        fullName: "",
        phoneNumber: "",
        emailAddress: "",
      },
      location: {
        address: "",
        community: ""
      },
      ghanaPostAddress: "",
      origin: "",
      reportedBy: {
        fullName: "",
        phoneNumber: "",
        emailAddress: "",
      },
      supportingImageUrls: [] as string[],
      notes: ""
    },
    validationSchema: SuspectedIllegalityValidationSchema,
    onSubmit: async (values) => {
      await createSuspectedIllegality({
        variables: {
          ...values,
          ...((values?.reportedBy?.fullName && values?.reportedBy.phoneNumber) 
            ? { reportedBy: values.reportedBy }
            : { reportedBy: undefined })
        },
      }).then(({ data }) => {
        if (data.createSuspectedIllegality._id) {
          toast(
            JSON.stringify({
              type: "success",
              title: "Suspected Illegality Created Successfully",
            })
          );
          refetch?.();
          form.resetForm();
        } else {
          toast(
            JSON.stringify({
              type: "error",
              title: "Could not create Suspected Illegality ",
            })
          );
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Report a Suspected Illegality"
      description="Provide the details to report a suspected illegality"
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={form.isSubmitting}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {form.isSubmitting ? "Reporting Suspected Illegality..." : "Report Suspected Illegality"}
          </button>
        </>
      )}
    >
      <SuspectedIllegalityForm form={form} />
    </Modal>
  );
}
